<template lang="pug">
  span.slb(v-if="labelNames.length")
    button.slb-trigger(
      ref="trigger"
      type="button"
      :class="'slb-trigger--' + size"
      :aria-label="title + ': ' + labelNames.join(', ')"
      :aria-describedby="open ? tooltipId : null"
      @mouseenter="onEnter"
      @mouseleave="onLeave"
      @focus="onEnter"
      @blur="onLeave"
      @click.stop.prevent="onClick"
      @keydown.esc="hideNow"
    )
      svg.slb-leaf(viewBox="0 0 173.63 213.74" aria-hidden="true")
        path(d="M28.19,147.01c-8.14,12.96-10.68,25.64-8.46,38.85,.86,5.1,2.52,10.06,4.04,15.03,1.78,5.83-.29,10.53-5.67,12.33-4.99,1.66-9.83-.73-12.16-6.02-16.05-36.44,1.39-80.7,40.69-100.43,12.63-6.34,26.79-10.01,40.25-14.9,2.73-.99,5.5-1.89,8.2-2.93,4.14-1.59,5.43-4.44,4.03-8.03-1.35-3.46-4.37-4.59-8.21-3.25-25.07,8.73-51.67,14.51-71.85,32.75-.21,.19-.65,.18-1.52,.4-.34-7.52,1.43-14.69,4.52-21.56,8.57-19.06,23.16-32.62,44.78-39.81,14.3-4.75,28.5-9.77,42.64-14.94,14.79-5.41,23.42-15.73,27.96-29.2,.63-1.88,2.19-3.54,3.31-5.3,1.9,1.16,4.37,1.94,5.62,3.54,28.66,36.34,35.79,76.29,16.37,119.97-9.41,21.16-26.46,36.48-49.78,45.51-28.43,11.01-61,3.99-80.07-16.88-1.33-1.46-2.66-2.92-4.67-5.13Z")
    .slb-tooltip(
      v-if="open"
      ref="tooltip"
      :id="tooltipId"
      role="tooltip"
      :class="'slb-tooltip--' + pos.placement"
      :style="{top: pos.top + 'px', left: pos.left + 'px'}"
    )
      .slb-tooltip-title {{ title }}
      ul.slb-tooltip-list
        li(v-for="name in labelNames" :key="name")
          i.fa.fa-check(aria-hidden="true")
          span {{ name }}
</template>

<script>
// Bola verde con la hoja + tooltip con las etiquetas de sostenibilidad del
// producto. Compartida por la tarjeta del catálogo y la ficha.
// Diseño portado de NEW-ECOMMERCE-FRONTEND (commits 70c41ac y 7da69fe).
export default {
  name: 'SustainabilityLabelBadge',
  props: {
    // Listado: nombres (product.sustainability_labels).
    // Ficha: objetos { id, name, … } (extra_info.sustainability.labels).
    labels: {
      type: Array,
      default: () => []
    },
    size: {
      type: String,
      default: 'sm' // 'xs' (22px) | 'sm' (24px) | 'md' (28px)
    }
  },
  data () {
    return {
      tooltipId: 'slb-' + this._uid,
      open: false,
      pinned: false, // abierto con clic / toque: sigue abierto hasta otro toque o clic fuera
      pos: {top: 0, left: 0, placement: 'below'}
    }
  },
  computed: {
    labelNames () {
      return (this.labels || [])
        .map(l => (typeof l === 'string' ? l : (l && (l.name || l.id))))
        .filter(Boolean)
    },
    title () {
      return this.$gettext('Certificados y etiquetas')
    }
  },
  beforeDestroy () {
    this.hideNow()
    // El tooltip vive en <body>: al destruir el componente Vue no lo quita.
    const tip = this.$refs.tooltip
    if (tip && tip.parentNode) {
      tip.parentNode.removeChild(tip)
    }
  },
  methods: {
    // Se mueve a <body> con posición fija: la tarjeta tiene overflow: hidden y
    // recortaría un tooltip posicionado dentro de ella.
    place () {
      this.$nextTick(() => {
        const trigger = this.$refs.trigger
        const tip = this.$refs.tooltip
        if (!trigger || !tip) return
        if (tip.parentNode !== document.body) {
          document.body.appendChild(tip)
        }
        const t = trigger.getBoundingClientRect()
        const gap = 8
        const margin = 8
        const w = tip.offsetWidth
        const h = tip.offsetHeight
        // clientWidth/Height y no innerWidth/Height: en móviles con la página más
        // ancha que la pantalla, inner* devuelven el tamaño sin zoom.
        const vw = document.documentElement.clientWidth
        const vh = document.documentElement.clientHeight
        const below = t.bottom + gap + h <= vh - margin
        const top = below ? t.bottom + gap : Math.max(margin, t.top - gap - h)
        // Alineado a la derecha del icono, que está en el borde derecho de la tarjeta.
        const left = Math.min(Math.max(margin, t.right - w), vw - w - margin)
        this.pos = {top: top, left: left, placement: below ? 'below' : 'above'}
      })
    },
    show () {
      this.open = true
      this.place()
      window.addEventListener('scroll', this.hideNow, true)
      window.addEventListener('resize', this.hideNow)
    },
    hideNow () {
      this.open = false
      this.pinned = false
      window.removeEventListener('scroll', this.hideNow, true)
      window.removeEventListener('resize', this.hideNow)
      document.removeEventListener('pointerdown', this.onOutside, true)
    },
    onEnter () {
      if (!this.pinned) this.show()
    },
    onLeave () {
      if (!this.pinned) this.hideNow()
    },
    // En pantallas táctiles no hay hover: un toque lo deja abierto. Nunca deja
    // pasar el clic a la tarjeta.
    onClick () {
      if (this.pinned) return this.hideNow()
      this.pinned = true
      this.show()
      document.addEventListener('pointerdown', this.onOutside, true)
    },
    onOutside (e) {
      const trigger = this.$refs.trigger
      const tip = this.$refs.tooltip
      if ((trigger && trigger.contains(e.target)) || (tip && tip.contains(e.target))) return
      this.hideNow()
    }
  }
}
</script>

<style lang="scss" scoped>
  .slb{
    display: inline-flex;
    flex-shrink: 0;
  }

  // Hoja de "Leaf - only ecolabel.svg". Su color (#cddb75) es demasiado claro
  // sobre blanco, así que pasa a ser el círculo y la hoja va en oscuro
  // (#4f6b12, ≈4:1 sobre #cddb75).
  .slb-trigger{
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    border: none;
    padding: 0;
    background: #cddb75;
    color: #4f6b12;
    cursor: default;
    transition: transform .15s ease;

    &:hover, &:focus-visible{
      transform: scale(1.08);
    }

    &:focus{
      outline: none;
    }

    &:focus-visible{
      outline: 2px solid #4f6b12;
      outline-offset: 2px;
    }

    &--xs{ width: 22px; height: 22px; }
    &--sm{ width: 24px; height: 24px; }
    &--md{ width: 28px; height: 28px; }
  }

  .slb-leaf{
    height: 58%;
    width: auto;

    path{
      fill: currentColor;
    }
  }

  // Los estilos scoped siguen aplicando tras moverlo a <body>.
  .slb-tooltip{
    position: fixed;
    z-index: 2000;
    max-width: 240px;
    padding: 7px 10px;
    // Verde marca claro con texto verde oscuro: #33470b sobre #cddb75 ≈ 6.9:1.
    background: #cddb75;
    color: #33470b;
    border-radius: 4px;
    text-align: left;
    animation: slb-in .12s ease both;
  }

  .slb-tooltip-title{
    font-size: 9.5px;
    font-weight: 600;
    letter-spacing: .06em;
    text-transform: uppercase;
    opacity: .8;
    margin-bottom: 4px;
  }

  .slb-tooltip-list{
    list-style: none;
    margin: 0;
    padding: 0;

    li{
      display: flex;
      align-items: flex-start;
      font-size: 11.5px;
      line-height: 1.3;

      &:not(:last-child){
        margin-bottom: 3px;
      }
    }

    i{
      flex-shrink: 0;
      font-size: 9px;
      margin: 3px 5px 0 0;
      color: #4f6b12;
    }
  }

  @keyframes slb-in{
    from{ opacity: 0; transform: translateY(2px); }
    to{ opacity: 1; transform: translateY(0); }
  }
</style>
